import React, {useCallback, useEffect} from "react";
import AppContainer from "../../common/app_container";
import DataTable from "react-data-table-component";
import {useAudioTrackAPI} from "@src/api/audio_track_api";
import {AudioTrack, AudioType} from "@src/model/audio_track";
import {useNavigate} from "react-router-dom";
import Alert, {AlertProps} from "../../common/alert";
import _ from "lodash";

interface AudioTrackIndexProps {
}

const AudioTrackIndex: React.FC<AudioTrackIndexProps> = () => {
    const [data, setData] = React.useState<AudioTrack[]>([]);
    const [filteredData, setFilteredData] = React.useState<AudioTrack[]>([]);
    const [search, setSearch] = React.useState("");
    const [status, setStatus] = React.useState<AlertProps | null>(null);
    const [loading, setLoading] = React.useState<boolean>(true);
    const audioTrackAPI = useAudioTrackAPI();
    const navigate = useNavigate();

    const columns = [
        {
            name: "Name",
            selector: (row: AudioTrack) => row.name || "",
            sortable: true,
            width: "200px",
        },
        {
            name: "Type",
            selector: (row: AudioTrack) => row.audio_type || "",
            sortable: true,
            width: "100px",
            cell: (row: AudioTrack) => (
                <span className={row.audio_type === AudioType.MUSIC ? "text-purple-500" : "text-blue-500"}>
          {row.audio_type}
        </span>
            ),
        },
        {
            name: "Description",
            selector: (row: AudioTrack) => row.description || "",
            sortable: true,
            width: "250px",
        },
        {
            name: "Tags",
            selector: (row: AudioTrack) => row.tags || "",
            sortable: true,
            width: "150px",
        },
        {
            name: "Public",
            selector: (row: AudioTrack) => row.is_public ? 1 : 0,
            sortable: true,
            cell: (row: AudioTrack) =>
                row.is_public ? (
                    <span className="text-green-500">Yes</span>
                ) : (
                    <span className="text-red-500">No</span>
                ),
            width: "80px",
        },
        {
            name: "Actions",
            cell: (row: AudioTrack) => (
                <div className="flex gap-2 flex-nowrap">
                    <button
                        onClick={() => navigate(`/audio-tracks/edit/${row.id}`)}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-2 rounded"
                    >
                        Edit
                    </button>
                    <button
                        onClick={() => handleDelete(row.id!)}
                        className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-2 rounded"
                    >
                        Delete
                    </button>
                </div>
            ),
            width: "180px",
        },
    ];

    const handleDelete = async (id: string) => {
        if (window.confirm("Are you sure you want to delete this audio track?")) {
            try {
                await audioTrackAPI.deleteAudioTrack(id);
                setStatus({type: "success", message: "Audio track deleted successfully"});
                fetchAudioTracks();
            } catch (error: any) {
                setStatus({type: "error", message: "Failed to delete audio track: " + error.message});
            }
        }
    };

    const fetchAudioTracks = useCallback(() => {
        setLoading(true);
        audioTrackAPI
            .getAllAudioTracks()
            .then((response) => {
                setData(response);
                setFilteredData(response);
                setLoading(false);
            })
            .catch((error) => {
                setStatus({type: "error", message: "Failed to load audio tracks: " + error.message});
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedFilter = useCallback(
        _.debounce((searchTerm: string) => {
            if (!searchTerm) {
                setFilteredData(data);
            } else {
                const lowerSearch = searchTerm.toLowerCase();
                setFilteredData(
                    data.filter(
                        (track) =>
                            track.name?.toLowerCase().includes(lowerSearch) ||
                            track.description?.toLowerCase().includes(lowerSearch) ||
                            track.tags?.toLowerCase().includes(lowerSearch)
                    )
                );
            }
        }, 300),
        [data]
    );

    useEffect(() => {
        fetchAudioTracks();
    }, [fetchAudioTracks]);

    useEffect(() => {
        debouncedFilter(search);
        return () => {
            debouncedFilter.cancel();
        };
    }, [search, debouncedFilter]);

    return (
        <AppContainer>
            <div className="container">
                <div className="flex justify-between">
                    <h1 className="text-3xl">Audio Tracks</h1>
                    <button
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                        onClick={() => navigate("/audio-tracks/add")}
                    >
                        Add Audio Track
                    </button>
                </div>
                {status && (
                    <div className="mb-4">
                        <Alert {...status} onClose={() => setStatus(null)}/>
                    </div>
                )}
                <form className="w-full mx-auto mt-2">
                    <label
                        htmlFor="default-search"
                        className="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white"
                    >
                        Search
                    </label>
                    <div className="relative">
                        <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                            <svg
                                className="w-4 h-4 text-gray-500 dark:text-gray-400"
                                aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                                />
                            </svg>
                        </div>
                        <input
                            type="search"
                            id="default-search"
                            onChange={(val) => setSearch(val.target.value)}
                            className="block w-full p-4 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                            placeholder="Search audio tracks by name, description, or tags..."
                            required
                        />
                    </div>
                </form>
                <DataTable
                    columns={columns}
                    data={filteredData}
                    pagination
                    progressPending={loading}
                    progressComponent={<div className="py-6">Loading audio tracks...</div>}
                    noDataComponent={<div className="py-6">No audio tracks found</div>}
                />
            </div>
        </AppContainer>
    );
};

export default AudioTrackIndex;
